import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { passwordSchema } from "@platform/validation";
import { Button, Input, Label } from "@nthamizh/ui";
import { useResetPassword } from "@platform/hooks";
import { ApiError } from "@platform/api-client";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CheckCircle2 } from "lucide-react";

const formSchema = z.object({ newPassword: passwordSchema });
type FormValues = z.infer<typeof formSchema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";
  const resetPassword = useResetPassword();
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setError("");
    try {
      await resetPassword.mutateAsync({ token, newPassword: values.newPassword });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "This link is invalid or has expired.");
    }
  });

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-[380px] text-center">
          <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-8">
            <h1 className="text-lg font-semibold text-gray-900">Invalid link</h1>
            <p className="mt-2 text-sm text-gray-500">This reset link is missing its token. Request a new one.</p>
            <Link to="/forgot-password" className="mt-6 inline-block text-sm font-medium text-blue-600 hover:text-blue-700">
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-[380px] text-center">
          <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-8">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">Password updated</h1>
            <p className="mt-2 text-sm text-gray-500">Sign in with your new password.</p>
            <Button onClick={() => navigate("/login")} className="mt-6">Sign in</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-[380px]">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Tailseed" className="h-16 w-16 object-contain" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Set a new password</h1>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="newPassword">New password</Label>
              <Input
                id="newPassword"
                type="password"
                {...register("newPassword")}
                error={errors.newPassword?.message}
              />
            </div>
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                {error}
                {error.toLowerCase().includes("expired") || error.toLowerCase().includes("invalid") ? (
                  <div className="mt-1.5">
                    <Link to="/forgot-password" className="font-medium underline hover:no-underline">
                      Request a new link
                    </Link>
                  </div>
                ) : null}
              </div>
            )}
            <Button type="submit" loading={resetPassword.isPending} className="w-full mt-1">
              Update password
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
